import {
  ConversationStatus,
  type ConversationPriority,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import type { MessageChannel } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import Asterisk02Icon from "@redotech/redo-icons/asterisk-02_filled.svg";
import ClockSnoozeIcon from "@redotech/redo-icons/clock-snooze_filled.svg";
import StatusIcon from "@redotech/redo-icons/status.svg";
import {
  RedoBadge,
  type RedoBadgeSize,
} from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import FolderCheckIcon from "@redotech/redo-web/icon-old/folder-check.svg";
import { memo } from "react";
import { ConversationStatusReadable } from "../../ui/conversation-status-ui";
import {
  MessageChannelIcons,
  MessageChannelReadable,
} from "../../ui/thread-channel-ui";

const statusConfig: Record<
  ConversationStatus,
  {
    color: "success" | "gray" | "warning" | "blue" | "error";
    Icon: typeof Asterisk02Icon;
  }
> = {
  OPEN: { color: "success", Icon: Asterisk02Icon },
  CLOSED: { color: "gray", Icon: FolderCheckIcon },
  SNOOZED: { color: "warning", Icon: ClockSnoozeIcon },
  IN_PROGRESS: { color: "blue", Icon: StatusIcon },
  HIDDEN: { color: "gray", Icon: FolderCheckIcon },
  ARCHIVED: { color: "gray", Icon: FolderCheckIcon },
  DELETED: { color: "gray", Icon: FolderCheckIcon },
  DRAFT: { color: "error", Icon: FolderCheckIcon },
};

export const StatusBadge = memo(function StatusBadge({
  status,
  size = "sm",
}: {
  status: ConversationStatus;
  size?: RedoBadgeSize;
}) {
  const { color, Icon } = statusConfig[status];
  return (
    <RedoBadge
      color={color}
      segmentLeading={{ type: "icon", Icon }}
      size={size}
      text={ConversationStatusReadable[status]}
    />
  );
});

export const ConversationPriorityReadable: Record<
  ConversationPriority,
  string
> = { URGENT: "Urgent" };

const priorityColorMap: Record<ConversationPriority, "error"> = {
  URGENT: "error",
};

export const PriorityBadge = memo(function PriorityBadge({
  priority,
  size = "sm",
}: {
  priority: ConversationPriority;
  size?: RedoBadgeSize;
}) {
  return (
    <RedoBadge
      color={priorityColorMap[priority]}
      size={size}
      text={ConversationPriorityReadable[priority]}
    />
  );
});

export const ChannelBadge = memo(function ChannelBadge({
  channel,
  size = "xs",
}: {
  channel: MessageChannel;
  size?: RedoBadgeSize;
}) {
  const displayName = MessageChannelReadable[channel];
  const Icon = MessageChannelIcons[channel];

  return (
    <RedoBadge
      color="gray"
      segmentLeading={{ type: "icon", Icon }}
      size={size}
      text={displayName}
    />
  );
});
